import React, { useState, useMemo } from 'react';
import { Dropdown, Spinner, Button, Alert } from 'react-bootstrap';
import moment from 'moment';
import { FiCopy } from 'react-icons/fi';
import DeleteModal from '../../../components/DeleteModal';
import Pagination from '../../../components/Pagination/Pagination';

const ContentPageList = ({ 
  content, 
  isLoading, 
  isError, 
  onAddNew, 
  onEdit,
  deleteContent,
  isDeleting,
  toggleStatus,
  isToggling,
  refetch
}) => {
  const itemsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalAction, setModalAction] = useState('delete');
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'info',
  });

  const showAlert = (message, variant = 'info') => {
    setAlert({ show: true, message, variant });
    setTimeout(
      () => setAlert({ show: false, message: '', variant: 'info' }),
      5000
    );
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showAlert('Copied to clipboard!', 'success');
    } catch (err) {
      showAlert('Failed to copy!', 'danger');
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      let success = false;
      if (modalAction === 'delete') {
        success = await deleteContent(selectedItem._id);
        if (success) {
          showAlert('Item deleted successfully!', 'success');
        }
      } else if (modalAction === 'toggle') {
        success = await toggleStatus(selectedItem._id);
        if (success) {
          showAlert('Status toggled successfully!', 'success');
        }
      }
      
      if (success) {
        setShowDeleteModal(false);
        await refetch();
      }
    } catch {
      showAlert(`Failed to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} item.`, 'danger');
    }
  };

  const handleToggleStatus = async (item) => {
    setSelectedItem(item);
    setModalAction('toggle');
    setShowDeleteModal(true);
  };

  const openDeleteModal = (item) => {
    setSelectedItem(item);
    setModalAction('delete');
    setShowDeleteModal(true);
  };

  const tableData = useMemo(() => {
    if (!Array.isArray(content)) return [];
    return content.map((item) => ({
      id: item._id || item.id,
      siteName: item.siteName || '-',
      pageName: item.pageName || '-',
      type: item.type || '-',
      isActive: item.isActive,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      fullItem: item,
    }));
  }, [content]);

  const filtered = tableData.filter((x) =>
    [x.siteName, x.pageName, x.type]
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginated = filtered.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  if (isError) {
    return (
      <div className="col-12">
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 text-danger">Error Loading Content</h5>
            <p className="text-muted">Failed to load content pages. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='col-12'>
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: '', variant: 'info' })
          }
          className='mb-3'
        >
          {alert.message}
        </Alert>
      )}

      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h4 className='card-title m-0'>Content Pages</h4>
          <div className='d-flex gap-2'>
            <input
              type='text'
              className='form-control w-auto'
              placeholder='Search content'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button onClick={onAddNew} type="button">
              + Add
            </Button>
          </div>
        </div>

        <div className='card-body'>
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Site</th>
                      <th>Page</th>
                      <th>Url</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Updated At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((item, i) => (
                      <tr key={item.id}>
                        <td>{currentPage * itemsPerPage + i + 1}</td>
                        <td>{item.siteName}</td>
                        <td>{item.pageName}</td>
                        <td className='d-flex align-items-center gap-2'>
                          <span>/content-page/{item.pageName}</span>
                          <Button
                            variant='link'
                            onClick={() =>
                              handleCopy(`/content-page/${item.pageName}`)
                            }
                            title='Copy URL'
                            type="button"
                          >
                            <FiCopy />
                          </Button>
                        </td>
                        <td>
                          <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{moment(item.createdAt).format('MMM DD, YYYY HH:mm')}</td>
                        <td>{moment(item.updatedAt).format('MMM DD, YYYY HH:mm')}</td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="link">
                              &#x2022;&#x2022;&#x2022;
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item
                                onClick={() => onEdit(item.fullItem)}
                              >
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleToggleStatus(item.fullItem)}
                              >
                                {item.fullItem.isActive ? 'Deactivate' : 'Activate'}
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => openDeleteModal(item.fullItem)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filtered.length}
                onPageChange={setCurrentPage}
              />

              {filtered.length === 0 && !isLoading && (
                <div className="text-center py-4">
                  <p className="text-muted">No content found</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isLoading={modalAction === 'delete' ? isDeleting : isToggling}
        title={modalAction === 'delete' ? 'Delete Content' : 'Toggle Status'}
        confirmText={modalAction === 'delete' ? 'Delete Content' : 'Yes'}
        message={
          selectedItem
            ? modalAction === 'delete'
              ? `Are you sure you want to delete "${selectedItem.pageName}"?`
              : `Are you sure you want to ${selectedItem.isActive ? 'deactivate' : 'activate'} "${selectedItem.pageName}"?`
            : `Are you sure you want to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} this item?`
        }
      />
    </div>
  );
};

export default ContentPageList;
